import { Component, Input, Output, EventEmitter, SimpleChanges, ChangeDetectionStrategy, SimpleChange, ViewChild, ElementRef, OnChanges, OnDestroy, inject, output, input } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ClipboardService } from 'ngx-clipboard';
import { ChartConfigBase } from '../../models/chart-config-base';
import { XmlReportDataService } from '../../services/xml-report-data.service';
import { HighChartDataBase } from '../../models/high-chart-data-base';
import { ClickDataPoint, ClickNotifier } from '../../models/click-notifier';
import { DrillDownInfo } from '../../models/drilldown-info';
import { ElearningInfo } from '../../models/elearning-info';

import * as Highcharts from 'highcharts'; // Obs! Används i template
import { CommonModule, AsyncPipe } from '@angular/common';
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap/nav';
import { SpinnerComponent } from '../spinner/spinner.component';
import { HighchartsChartComponent } from 'highcharts-angular';
import { ChartTotalSumComponent } from '../chart-total-sum/chart-total-sum.component';
import { ChartInfoComponent } from '../chart-info/chart-info.component';
import { ChartHeightComponent } from '../chart-height/chart-height.component';
import { ChartShowTableComponent } from '../chart-show-table/chart-show-table.component';
import { ChartShowDetailedTableComponent } from '../chart-show-detailed-table/chart-show-detailed-table.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { structuredMerge } from '../../../../../../../src/functions/structuredMerge';
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { faChartBar, faCopy, faInfoCircle, faQuestion, faTable } from "@fortawesome/free-solid-svg-icons";

@Component({
    selector: 'xml-report',
    styleUrls: ['./xml-report.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './xml-report.html',
    imports: [NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, SpinnerComponent, HighchartsChartComponent, ChartTotalSumComponent, ChartInfoComponent, ChartHeightComponent, ChartShowTableComponent, ChartShowDetailedTableComponent, NgbNavOutlet, AsyncPipe, TranslatePipe, FaIconComponent]
})
export class XmlReportComponent implements OnChanges, OnDestroy {
    private readonly xmlService = inject(XmlReportDataService);
    private readonly clipboardService = inject(ClipboardService);
    private readonly translate = inject(TranslateService);

    faChartBar = faChartBar;
    faTable = faTable;
    faQuestion = faQuestion;
    faCopy = faCopy;
    faInfoCircle = faInfoCircle;

    config = input<ChartConfigBase>();
    productionMode = input<boolean>(false);
    chartOnly = input<boolean>(false);
    currentChartHeight = input<string>();
    drillDownInfo = input<DrillDownInfo>();
    language = input<string>();
    
    exportChartData = output<HighChartDataBase>();
    clickedCategory = output<string>();
    clickedSerie = output<ClickDataPoint>();
    @Output() loadReportError = new EventEmitter();
    @Output() drillUp = new EventEmitter();
    @Output() chartHeightChange = new EventEmitter();

    @ViewChild('code', {static: false}) code: ElementRef;

    activeTab = 1;
    highcharts = Highcharts;
    chartOptions = new BehaviorSubject<Object>(null);
    chartOptionsIsUpdated: boolean = false;
    chartData = new BehaviorSubject<HighChartDataBase>(null);
    descriptionHtml = new BehaviorSubject<string>(null);
    isLoading = new BehaviorSubject<boolean>(null);
    isShowingSql: boolean = false;
    isShowingTable = false;
    showInfo: boolean = false;

    public clickNotifier = new ClickNotifier((clickedPoint: ClickDataPoint) => {
        this.clickedCategory.emit(clickedPoint.category);
        if (clickedPoint.serie) this.clickedSerie.emit(clickedPoint);
    });

    public sqlIsCopied: boolean = false;


    ngOnChanges(changes: SimpleChanges): void {
        let config: SimpleChange = changes['config'];
        if (config) {
            if (config.previousValue &&
                (config.currentValue['reportName'] !== config.previousValue['reportName'] ||
                config.currentValue['language'] !== config.previousValue['language'])) {
                this.xmlService.currentGroupings.next(null); // Ny rapport eller nytt språk, rensa groupings innan uppdatering
            }
            this.updateChart();
        }
    }

    ngAfterViewChecked(): void {
        if (this.drillDownInfo().canDrillDown)
            this.setCursorOnChartPoints();
    }

    ngOnDestroy() {
        this.getChartDataSubscription?.unsubscribe();
    }

    public reportHasErrors = new BehaviorSubject<boolean>(false);
    public reportHasBelowMinDataAmount = new BehaviorSubject<boolean>(false);
    public reportError: string;
    private getChartDataSubscription: Subscription;
    public updateChart() {
        if (!this.config() || !this.config().reportName) {
            return;
        }
        this.reportHasErrors.next(false);
        this.reportHasBelowMinDataAmount.next(false);

        this.isLoading.next(true);

        if (this.getChartDataSubscription) {
            //Om ett anrop redan är ivägskickat, avbryt det anropet innan ett nytt skickas iväg
            this.getChartDataSubscription.unsubscribe();
        }

        const options = this.getTranslations();

        this.getChartDataSubscription = this.xmlService.getHighChartData(this.config(), this.clickNotifier).subscribe({
            next: (data: HighChartDataBase) => {
                this.chartData.next(data); // Spara ner för ev framtida bruk
                this.exportChartData.emit(data); // Skicka chartdata till utanför komponenten
                const opts = data.getHighChartOptions();

                structuredMerge(opts, options); // Slå ihop defaultoptions med options från servern så att defaultoptions alltid är med
//                console.log(opts);

                this.chartOptions.next(opts); // Hämta ut graf-data + options och skicka till highcharts
                this.chartOptionsIsUpdated = true;
                if (this.chartOnly() == false) {
                    this.descriptionHtml.next(data.descriptionHtml);
                    this.sqlIsCopied = false;
                    this.activeTab = 1;
                }
                this.isLoading.next(false);
            }, error: (error) => {
                this.reportHasErrors.next(true);
                this.reportError = error;
                if (error && (typeof(error) === 'string') && (error.indexOf('datamängd') > -1 || error.indexOf('data amount') > -1)) {
                    this.reportHasBelowMinDataAmount.next(true);
                }
                this.isLoading.next(false);
            }
        });
    }

    public updateIsLoading(isLoading: boolean) {
        this.isLoading.next(isLoading);
    }

    public infoZoomScrollIsRelevant(): boolean {
        let isRelevant = true;
        let chartData = this.chartData.getValue();
        if (!chartData) return false;
        if (chartData.chartType == "radar") isRelevant = false;
        return isRelevant;
    }

    public copySql(): void {
        let codeText = (this.code != undefined) ? this.code.nativeElement.innerText : "";
        this.clipboardService.copy(codeText);
        this.sqlIsCopied = true;
    }

    public getExcel(): void {
        let data = this.chartData.getValue();
        this.xmlService.getExcelTable(this.config()).subscribe((blob: Blob) => {
            const fileName = `${data.chartTitle}-${data.chartSubTitle}.xlsx`;
            const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
            let url = window.URL.createObjectURL(blob);
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        });
    }

    private setCursorOnChartPoints() {
        let chartPoints = document.getElementsByClassName("highcharts-point");
        for (var i = 0; i < chartPoints.length; i++) {
            chartPoints[i].setAttribute("style", "cursor: pointer");
        }
    }


    private getTranslations(): Object {
        return  {
            lang: {
                locale: this.translate.getCurrentLang(),
                contextButtonTitle: this.translate.instant('xml-report.chart.highcharts.contextButtonTitle'),
                decimalPoint: this.translate.instant('xml-report.chart.highcharts.decimalPoint'),
                downloadJPEG: this.translate.instant('xml-report.chart.highcharts.downloadJPEG'),
                downloadPDF: this.translate.instant('xml-report.chart.highcharts.downloadPDF'),
                downloadPNG: this.translate.instant('xml-report.chart.highcharts.downloadPNG'),
                downloadSVG: this.translate.instant('xml-report.chart.highcharts.downloadSVG'),
                drillUpText: this.translate.instant('xml-report.chart.highcharts.drillUpText', {serie: '{series.name}'}),
                invalidDate: this.translate.instant('xml-report.chart.highcharts.invalidDate'),
                loading: this.translate.instant('xml-report.chart.highcharts.loading'),
                months: [this.translate.instant('months.01'),
                        this.translate.instant('months.02'),
                        this.translate.instant('months.03'),
                        this.translate.instant('months.04'),
                        this.translate.instant('months.05'),
                        this.translate.instant('months.06'),
                        this.translate.instant('months.07'),
                        this.translate.instant('months.08'),
                        this.translate.instant('months.09'),
                        this.translate.instant('months.10'),
                        this.translate.instant('months.11'),
                        this.translate.instant('months.12')],
                noData: this.translate.instant('xml-report.chart.highcharts.noData'),
                numericSymbols: null,
                printChart: this.translate.instant('xml-report.chart.highcharts.printChart'),
                resetZoom: this.translate.instant('xml-report.chart.highcharts.resetZoom'),
                resetZoomTitle: this.translate.instant('xml-report.chart.highcharts.resetZoomTitle'),
                shortMonths: [this.translate.instant('months.short.01'),
                            this.translate.instant('months.short.02'),
                            this.translate.instant('months.short.03'),
                            this.translate.instant('months.short.04'),
                            this.translate.instant('months.short.05'),
                            this.translate.instant('months.short.06'),
                            this.translate.instant('months.short.07'),
                            this.translate.instant('months.short.08'),
                            this.translate.instant('months.short.09'),
                            this.translate.instant('months.short.10'),
                            this.translate.instant('months.short.11'),
                            this.translate.instant('months.short.12')],
                thousandsSep: this.translate.instant('xml-report.chart.highcharts.thousandsSep'),
                weekdays: [this.translate.instant('weekdays.07'),
                        this.translate.instant('weekdays.01'),
                        this.translate.instant('weekdays.02'),
                        this.translate.instant('weekdays.03'),
                        this.translate.instant('weekdays.04'),
                        this.translate.instant('weekdays.05'),
                        this.translate.instant('weekdays.06')]
            }
        };
    }    
}
